import { Outlet, useLocation } from "react-router-dom";
import { Footer, Header } from "../ComponentFile";
import Sidebar from "./Sidebar/Sidebar";
import CustomerSidebar from "./Sidebar/CustomerSidebar";
import CustomerHeader from "./Header/CustomerHeader";
import useAuth from "../Hooks/useAuth";
import { useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollSmoother } from "gsap/ScrollSmoother";
import {
  BUSINESS_MAIN_HEADER_OFFSET_CLASS,
  isBusinessCenteredContentPath,
} from "../sections/BusinessDashboard/shared/businessLayoutSpec";
import {
  CUSTOMER_MAIN_HEADER_OFFSET_CLASS,
  isCustomerCenteredContentPath,
} from "../sections/CustomerDashboard/shared/customerLayoutSpec";
import {
  isPublicFooterSpacingPath,
  PUBLIC_FOOTER_SPACING_PROPS,
} from "../sections/Public/shared/publicLayoutSpec";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

const Layout = () => {
  const location = useLocation();
  const { auth } = useAuth();
  const footerSpacingProps = isPublicFooterSpacingPath(location.pathname)
    ? PUBLIC_FOOTER_SPACING_PROPS
    : {};

  useEffect(() => {
    let smoother = null;
    let style = null;

    if (location.pathname === "/business-support-centre" || location.pathname === "/customer-support-centre") {
      // Add required CSS for ScrollSmoother
      style = document.createElement('style');
      style.textContent = `
        #smooth-wrapper {
          overflow: hidden;
          position: fixed;
          height: 100%;
          width: 100%;
          top: 0;
          left: 0;
          z-index: 1;
        }
        #smooth-content {
          overflow: visible;
          width: 100%;
        }
      `;
      document.head.appendChild(style);

      // Initialize GSAP ScrollSmoother
      smoother = ScrollSmoother.create({
        wrapper: "#smooth-wrapper",
        content: "#smooth-content",
        smooth: 1.5,
        effects: true,
        smoothTouch: 0.1,
      });
    }

    return () => {
      if (smoother) {
        smoother.kill();
      }
      if (style && style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, [location.pathname]);

  // Add CSS class to html tag for chats page
  useEffect(() => {
    const isChatsPage = location.pathname.includes("dashboard");
    
    if (isChatsPage) {
      document.documentElement.classList.add('chats-page-full-height');
      document.documentElement.style.height = '100%';
      document.documentElement.style.background = 'white';
    } else {
      document.documentElement.classList.remove('chats-page-full-height');
      document.documentElement.style.height = '';
      document.documentElement.style.background = '';
    }
    
    // Cleanup on unmount
    return () => {
      document.documentElement.classList.remove('chats-page-full-height');
      document.documentElement.style.height = '';
      document.documentElement.style.background = '';
    };
  }, [location.pathname]);

  // Business dashboard layout
  if (location?.pathname?.includes("business-dashboard")) {
    const isChatsPage = location.pathname === "/business-dashboard/inbox/chats";
    const isQuotesPage = location.pathname === "/business-dashboard/inbox/quotes";
    const isDeletedPage = location.pathname === "/business-dashboard/inbox/deleted";
    const isBlockedPage = location.pathname === "/business-dashboard/inbox/blocked";
    const isCreatePostPage = location.pathname.includes("/business-dashboard/posts/create-post");
    const isCreateTicketPage = location.pathname.includes("/business-dashboard/tickets/create");
    const isTicketsListPage = /^\/business-dashboard\/tickets\/(all|open|new|resolved|closed)$/.test(location.pathname);
    const isTicketDetailPage = /\/business-dashboard\/tickets\/[^/]+$/.test(location.pathname) && !isTicketsListPage && !isCreateTicketPage;
    const isDraftsPage = location.pathname === "/business-dashboard/posts/drafts";
    const isPublishedPage = location.pathname === "/business-dashboard/posts/published";
    const isBinPage = location.pathname === "/business-dashboard/posts/bin";
    const isFlaggedPage = location.pathname === "/business-dashboard/posts/flagged";
    const isReviewPage = location.pathname === "/business-dashboard/inbox/reviews";
    const isBillingDetailsPage = location.pathname === "/business-dashboard/subscriptions/billing-details";
    const isPortfolioPage = location.pathname === "/business-dashboard/settings/manage-portfolio";
    const isCenteredContentPage = isBusinessCenteredContentPath(location.pathname);
    const hideSidebar = isChatsPage || isQuotesPage || isDeletedPage || isBlockedPage || isCreatePostPage || isCreateTicketPage || isTicketsListPage || isTicketDetailPage || isDraftsPage || isPublishedPage || isBinPage || isFlaggedPage || isReviewPage || isBillingDetailsPage || isPortfolioPage;
    const isViewingMessage = new URLSearchParams(location.search).get('view') === 'message';
    const hideHeader = (isChatsPage || isQuotesPage) ? false : isViewingMessage;
    const businessHeaderOffsetClass =
      hideHeader || isCenteredContentPage ? '' : BUSINESS_MAIN_HEADER_OFFSET_CLASS;
    
    return (
      <main className={`App dashboard-panel font-inter lg:grid bg-white ${hideSidebar ? "pb-0" : "pb-10"}`} id="smooth-wrapper">
        <div id="smooth-content">
          {!hideHeader && <Header isSellerDashboard />}
          <div className={`${hideSidebar ? "lg:block" : "lg:grid lg:grid-cols-[0fr,_1fr]"} lg:mx-auto ${location.pathname === "/business-dashboard/dashboard" ? "tp-container gap-12" : (isDraftsPage || isPublishedPage || isBinPage || isFlaggedPage || location.pathname === "/business-dashboard/settings/manage-portfolio") ? "tp-container" : isReviewPage ? "tp-container-small gap-10" : hideSidebar ? "tp-container-small" : "tp-container-main gap-10"} hidden-scrollbar ${businessHeaderOffsetClass}`}>
            {!hideSidebar && (
              <div className="hidden lg:block">
                <Sidebar />
              </div>
            )}
            <div className={`lg:mx-auto w-full min-w-0 max-w-full ${hideSidebar ? "pb-0" : "pb-20"}`}>
              <Outlet />
            </div>
          </div>
        </div>
      </main>
    );
  }

  // Customer dashboard layout
  if (location?.pathname?.includes("customer-dashboard")) {
    const isChatsPage = location.pathname === "/customer-dashboard/inbox/chats";
    const isQuotesPage = location.pathname === "/customer-dashboard/inbox/quotes";
    const isDeletedPage = location.pathname === "/customer-dashboard/inbox/deleted";
    const isBlockedPage = location.pathname === "/customer-dashboard/inbox/blocked";
    const isSavedPostsPage = location.pathname === "/customer-dashboard/posts/saved";
    const isRecentlyViewedPostsPage = location.pathname === "/customer-dashboard/posts/recently-viewed";
    const isReviewPage = location.pathname === "/customer-dashboard/inbox/reviews";
    const isCreateTicketPage = location.pathname === "/customer-dashboard/tickets/create";
    const isTicketsListPage = /^\/customer-dashboard\/tickets\/(all|open|new|resolved|closed)$/.test(location.pathname);
    const isCustomerTicketDetailPage = /^\/customer-dashboard\/tickets\/[^/]+$/.test(location.pathname) && !isTicketsListPage && !isCreateTicketPage;
    const isCenteredContentPage = isCustomerCenteredContentPath(location.pathname);
    const hideSidebar = isChatsPage || isQuotesPage || isDeletedPage || isBlockedPage || isSavedPostsPage || isRecentlyViewedPostsPage || isReviewPage || isCreateTicketPage || isTicketsListPage || isCustomerTicketDetailPage;
    const isViewingMessage = new URLSearchParams(location.search).get('view') === 'message';
    const hideHeader = (isChatsPage || isQuotesPage) ? false : isViewingMessage;
    const customerHeaderOffsetClass =
      hideHeader || isCenteredContentPage ? '' : CUSTOMER_MAIN_HEADER_OFFSET_CLASS;

    return (
      <main className={`App dashboard-panel font-inter lg:grid bg-white ${hideSidebar ? "pb-0" : "pb-10"}`} id="smooth-wrapper">
        <div id="smooth-content">
          {!hideHeader && <CustomerHeader isCustomerDashboard />}
          <div className={`${hideSidebar ? "lg:block" : "lg:grid lg:grid-cols-[0fr,_1fr]"} lg:mx-auto ${location.pathname === "/customer-dashboard/dashboard" ? "tp-container gap-12" : (isSavedPostsPage || isRecentlyViewedPostsPage) ? "tp-container" : (isReviewPage || isChatsPage || isQuotesPage || isDeletedPage || isCreateTicketPage || isTicketsListPage || isCustomerTicketDetailPage) ? "tp-container-small gap-10" : hideSidebar ? "tp-container-small" : "tp-container-main gap-10"} hidden-scrollbar ${customerHeaderOffsetClass}`}>
            {!hideSidebar && (
              <div className="hidden lg:block">
                <CustomerSidebar />
              </div>
            )}
            <div className={`lg:mx-auto w-full ${hideSidebar ? "pb-0" : "pb-20"}`}>
              <Outlet />
            </div>
          </div>
        </div>
      </main>
    );
  }

  // Public layout (not authenticated)
  if (!auth?.user) {
    const isPricingPage = location.pathname === "/pricing";
    return (
      <main className={`App ${isPricingPage ? 'bg-mistygray' : ''}`} id="smooth-wrapper">
        <div id="smooth-content">
          <Header />
          <Outlet />
          {!auth?.user && <>{location.pathname !== "/" && <Footer {...footerSpacingProps} />}</>}
        </div>
      </main>
    );
  }

  // Authenticated user layout
  const isPricingPage = location.pathname === "/pricing";
  return (
    <main className={`App ${isPricingPage ? 'bg-mistygray' : 'bg-pastalblue'}`} id="smooth-wrapper">
      <div id="smooth-content">
        {auth?.roleType === "customer" ? (
          <CustomerHeader />
        ) : (
          <Header />
        )}
        <Outlet />
        {<>{location.pathname !== "/" && <Footer {...footerSpacingProps} />}</>}
      </div>
    </main>
  );
};

export default Layout;
